import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Row,
  Col,
  InputGroup,
  Card,
  CardBody,
  FormGroup,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { canAccessBatchImport } from "../../utils/permissionUtils";
import { API_ENDPOINTS } from "../../constant/_constant";
import usePostRequest from "../../hooks/usePostRequest";
import moment from "moment";
import {
  IconBox,
  IconCopyCheck,
  IconEdit,
  IconEye,
  IconMessageCircle,
  IconPaperclip,
  IconTrash,
  IconLayoutListFilled,
  IconLayoutGridFilled,
  IconPencil,
  IconChevronRight,
  IconCopy,
  IconDotsVertical,
} from "@tabler/icons-react";
import View_DetailsProduct from "./ViewDetail/View_DetailsProduct";
import useIdleLogout from "../../hooks/useIdleLogout";
import UpdateProductsPopup from "./UpdateProductsPopup";
import ComanConfirmationModal from "../../components/ComanConfirmationModal";
import SkuListing from "./SkuListing";

function SkuListUI() {
  const user = useSelector((state) => state.auth.user);
  const hasBatchImportAccess = canAccessBatchImport(user);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isClicked, setIsClicked] = useState(false);
  const [isListView, setIsListView] = useState(true);
  const [productData, setProductData] = useState([]);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);

  const [isViewOpen, setIsViewOpen] = useState({
    show: false,
    order: [],
    isStatusUpdate: false,
  });
  const [isUpdateProd, setIsUpdateProd] = useState({
    show: false,
    prodId: "",
    skuId: "",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState({
    show: false,
    action: null,
    message: "",
  });

  const [sku, setSku] = useState({
    vendor: "",
    skuId: "",
    type: "",
  });

  const { data: vendorlist, postData: fetchVendorList } = usePostRequest(
    API_ENDPOINTS.GET_VENDORS,
  );

  const {
    data: vendors,
    isLoading: loadingProducts,
    postData: fetchVendors,
  } = usePostRequest(API_ENDPOINTS.GET_SKU_LISTING);

  const toggleDropdown = (id) => {
    const isOpening = openDropdown !== id;
    setOpenDropdown(isOpening ? id : null);
    if (tableWrapperRefs.row1?.current) {
      tableWrapperRefs.row1.current.style.overflow = isOpening
        ? "visible"
        : "auto";
    }
  };

  const onSuccessfulFetchProducts = (response, reset = false) => {
    if (!response?.data || response.data.length < limit) {
      setHasMoreProducts(false);
    } else {
      setHasMoreProducts(true);
    }
    setProductData((prevData) => {
      const combined = reset
        ? response?.data || []
        : [...prevData, ...(response?.data || [])];
      const unique = Array.from(
        new Map(combined.map((item) => [item.sku_id, item])).values(),
      );
      return unique;
    });
  };

  const handleLimitChange = (selectedLimit) => {
    setLimit(selectedLimit);
  };

  useEffect(() => {
    fetchVendorList({ page: 1, limit: 500, is_relevent: true });
  }, []);

  useEffect(() => {
    const requestParams = {
      page,
      limit,
      vendor_id: sku.vendor || null,
      search: sku.skuId || null,
      type: sku.type || null,
    };

    fetchVendors(requestParams, (response) =>
      onSuccessfulFetchProducts(response, page === 1),
    );
    setIsClicked(false);
  }, [page, limit, isClicked]);

  const handleLoadMore = () => {
    if (hasMoreProducts && !loadingProducts) {
      setPage((prev) => prev + 1);
    }
  };

  const handleClearFilter = () => {
    setSku({
      vendor: "",
      skuId: "",
      type: "",
    });
    setPage(1);
    setProductData([]);
    setHasMoreProducts(true);
    setIsClicked((prev) => !prev);
  };

  const handleSearch = () => {
    setPage(1);
    setProductData([]);
    setHasMoreProducts(true);
    setIsClicked((prev) => !prev);
  };

  const handleViewClick = (vendor) => {
    handleClickOutside();
    setIsViewOpen({
      show: true,
      products: vendor,
    });

    const currentUrl = window.location.pathname;
    const newUrl = `${currentUrl}?${vendor.sku_id}`;
    window.history.pushState(null, "", newUrl);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSku((prevSku) => ({
      ...prevSku,
      [name]: value,
    }));
  };

  const truncateString = (str, num) => {
    if (!str) return "";
    if (str.length > num) {
      return str.slice(0, num) + "...";
    }
    return str;
  };

  useIdleLogout();

  const handleUpdateClick = (sku_id) => {
    navigate(`/update-product/${sku_id}`);
  };

  const openModalCopy = (product) => {
    let modalMessage = `Are you sure you want to duplicate this "${product?.sku_id}" product?`;
    let modalTitle = "Duplicate Order";
    let type = "success";
    let modalIcon = <IconCopy stroke={1} />;
    const productId = product?.sku_id;
    let actionHandler = () => {
      navigate("/create-product", { state: { productId } });
    };
    let action = "Change";
    setIsModalOpen({
      show: true,
      action,
      message: modalMessage,
      onConfirm: actionHandler,
      title: modalTitle,
      icon: modalIcon,
      type: type,
    });
  };

  const tableWrapperRefs = {
    row1: useRef(null),
  };
  const dropdownRef = useRef(null);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event?.target)) {
      setOpenDropdown(null);
      if (tableWrapperRefs.row1?.current) {
        tableWrapperRefs.row1.current.style.overflow = "auto";
      }
    }
  };
  useEffect(() => {
    if (!openDropdown) return;
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  return (
    <>
      <ComanConfirmationModal
        show={isModalOpen.show}
        onHide={() => {
          setIsModalOpen({ ...isModalOpen, show: false });
          setIsOpen(false);
        }}
        title={isModalOpen.title}
        message={isModalOpen.message}
        onConfirm={isModalOpen.onConfirm}
        icon={isModalOpen.icon}
        type={isModalOpen.type}
      />

      {isUpdateProd.show && (
        <UpdateProductsPopup
          show={isUpdateProd.show}
          skuId={isUpdateProd.skuId}
          productId={isUpdateProd.prodId}
          setShow={setIsUpdateProd}
          onDeleteSuccess={handleClearFilter}
        />
      )}
      <View_DetailsProduct
        isStatusUpdate={isViewOpen.isStatusUpdate}
        onStatusUpdate={() =>
          setIsViewOpen((prev) => ({ ...prev, isStatusUpdate: true }))
        }
      />

      {isListView ? (
        <div className="page-header">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Products</h2>
            </div>
          </div>
        </div>
      ) : null}

      {isListView ? (
        <Card className="mb-3">
          <CardBody>
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            >
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Form.Label className="">Search</Form.Label>
                    <InputGroup className="mb-3">
                      <Form.Control
                        name="skuId"
                        value={sku.skuId}
                        onChange={handleInputChange}
                        placeholder="Search...."
                        aria-label="sku-id"
                        aria-describedby="basic-addon1"
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Form.Label className="">Select Vendor</Form.Label>
                    <Form.Select
                      name="vendor"
                      value={sku.vendor}
                      onChange={handleInputChange}
                      aria-label="Select Vendor"
                    >
                      <option value="">All</option>
                      {vendorlist?.data?.map((vendor) => (
                        <option
                          key={vendor.VendorContact?.vendor_id}
                          value={vendor.VendorContact?.vendor_id ?? 0}
                        >
                          {vendor.display_name}
                        </option>
                      ))}
                    </Form.Select>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Form.Label className="">Product Type</Form.Label>
                    <Form.Select
                      name="type"
                      value={sku.type}
                      onChange={handleInputChange}
                      aria-label="Select Type"
                    >
                      <option value="">All</option>
                      <option value="inventory">Inventory</option>
                      <option value="non-inventory">Non-Inventory</option>
                      <option value="kit">Kit</option>
                    </Form.Select>
                  </FormGroup>
                </Col>
                <Col md={3} className="mb-2">
                  <Form.Label className="form-label">&nbsp;</Form.Label>
                  <div className="d-flex gap-2">
                    <Button
                      className="w-100"
                      variant="warning"
                      onClick={handleSearch}
                    >
                      Apply Filters
                    </Button>
                    <Button
                      className="w-100"
                      variant="outline-secondary"
                      onClick={handleClearFilter}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </Col>
                <Form.Text>
                  Search by SKU, name, description, vendor name, product type,
                  or any other relevant keyword.
                </Form.Text>
              </Row>
            </Form>
          </CardBody>
        </Card>
      ) : null}

      {isListView ? (
        <div className="d-flex align-items-center justify-content-end pt-2 pb-3 gap-3">
          <div className="view-toggle d-flex align-items-center justify-content-end gap-1">
            <IconLayoutGridFilled
              onClick={() => setIsListView(false)}
              style={{
                cursor: "pointer",
                color: !isListView ? "#FE6338" : "#515151",
              }}
            />
            <IconLayoutListFilled
              onClick={() => setIsListView(true)}
              style={{
                cursor: "pointer",
                color: isListView ? "#FE6338" : "#515151",
              }}
            />
          </div>
        </div>
      ) : null}

      {isListView ? (
        <div className="order_list_view">
          <div
            className="table-responsive mail_wrapper border-0 p-0"
            ref={tableWrapperRefs.row1}
            style={{ overflowX: "auto" }}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Vendor Name</th>
                  <th>Product Type</th>
                  <th>Cost</th>
                  <th>Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {productData.length > 0 ? (
                  productData.map((row, index) => {
                    return (
                      <tr key={index}>
                        <td className="text-orange">{row?.name || "N/A"}</td>
                        <td className="text-orange">
                          #{row?.sku_display_name || "NA"}
                        </td>
                        <td>{row?.Vendor?.display_name || "N/A"}</td>
                        <td>
                          {row?.type
                            ? row.type.charAt(0).toUpperCase() +
                            row.type.slice(1)
                            : "N/A"}
                        </td>
                        <td>$ {row?.aspetto_price || "0.00"}</td>
                        <td>$ {row?.customer_price || "0.00"}</td>
                        <td style={{ position: "relative" }}>
                          <button
                            onClick={() => toggleDropdown(row.sku_id)}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            <IconDotsVertical size={20} />
                          </button>
                          {openDropdown === row.sku_id && (
                            <ul
                              ref={dropdownRef}
                              className="order-dropdown-menu p-0"
                            >
                              {hasBatchImportAccess && (
                              <li
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateClick(row.sku_id);
                                }}
                              >
                                <span>
                                  <IconPencil stroke={1} /> Edit
                                </span>
                                <IconChevronRight stroke={1} />
                              </li>
                              )}
                              <li
                                className="dropdown-item border-top"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewClick(row);
                                }}
                              >
                                <span>
                                  <IconEye stroke={1} /> View Details
                                </span>
                                <IconChevronRight stroke={1} />
                              </li>
                              <li
                                className="dropdown-item border-top"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOutside();
                                  openModalCopy(row);
                                }}
                              >
                                <span>
                                  <IconCopyCheck stroke={1} /> Duplicate
                                </span>
                                <IconChevronRight stroke={1} />
                              </li>
                              <li
                                className="dropdown-item border-top"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOutside();
                                  setIsUpdateProd({
                                    show: true,
                                    prodId: row?.id,
                                    skuId: row?.sku_id,
                                  });
                                }}
                              >
                                <span>
                                  <IconTrash stroke={1} /> Delete
                                </span>
                                <IconChevronRight stroke={1} />
                              </li>
                              <li className="border-top">
                                <div className="rfq_lis_short justify-content-center p-2">
                                  {row.type === "kit" && (
                                    <span>
                                      <IconBox stroke={1} />{" "}
                                      {row.ProductKits?.length ?? 0}
                                    </span>
                                  )}
                                  <span>
                                    <IconPaperclip stroke={1} />{" "}
                                    {row.Attachments?.length ?? 0}
                                  </span>
                                  <span>
                                    <IconMessageCircle stroke={1} />{" "}
                                    {row.section.reduce((acc, section) => {
                                      return acc + section.notes.length;
                                    }, 0)}
                                  </span>
                                </div>
                              </li>
                            </ul>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No matching records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {productData.length >= 10 && hasMoreProducts && (
              <div className="text-center mt-3">
                <Button
                  variant="outline-primary"
                  onClick={handleLoadMore}
                  disabled={loadingProducts}
                >
                  {loadingProducts ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <SkuListing />
      )}
    </>
  );
}

export default SkuListUI;